import {
  FB_PIXEL_ID,
  GA_MEASUREMENT_ID,
  GOOGLE_ADS_ID,
  MARKETING_ENABLED,
  PINTEREST_TAG_ID,
  PROFITWELL_TOKEN,
} from '@/lib/env';
import {
  identifyFacebookPixel,
  loadFacebookPixel,
  trackFacebookCompleteRegistration,
  trackFacebookPageView,
  trackFacebookSubscribe,
} from '@/lib/facebookPixel';
import {
  identifyGoogleAnalytics,
  loadGoogleAnalytics,
  resetGoogleAnalytics,
  trackGoogleAnalyticsPageView,
  trackGoogleSignup,
  trackGoogleSubscribe,
} from '@/lib/googleAnalytics';
import {
  identifyPinterest,
  loadPinterest,
  trackPinterestCheckout,
  trackPinterestPageVisit,
  trackPinterestSignup,
} from '@/lib/pinterest';
import { identifyProfitWell, loadProfitWell } from '@/lib/profitwell';
import { isImpersonating } from '@/lib/getActiveUid';

// Thin orchestrator over the ad/conversion tracking providers (GA4, Google Ads,
// Facebook Pixel, Pinterest). dabble-accounts owns signup + checkout, so this is
// where the registration and subscribe conversions fire. Everything no-ops until
// `initMarketing` runs — which only happens on the production host — so identify
// / pageview / conversion calls are safe to make unconditionally elsewhere.

interface MarketingUser {
  uid: string;
  email?: string;
}

interface SubscribeDetails {
  value: number;
  currency: string;
  transactionId?: string;
  planId?: string;
  planName?: string;
}

let active = false;

/**
 * Load the configured tracking pixels once, at app boot. Gated to the production
 * host (`MARKETING_ENABLED`); off-production, or with no ids configured, this is
 * a no-op and everything below stays inert.
 */
export function initMarketing() {
  // Skip impersonated ("view as") tabs entirely: leaving `active` false makes
  // identify / page views / conversions all no-op, so an admin session never
  // fires real events — covering every entry point, not just identify.
  if (active || !MARKETING_ENABLED || isImpersonating()) return;
  if (!GA_MEASUREMENT_ID && !FB_PIXEL_ID && !PINTEREST_TAG_ID && !PROFITWELL_TOKEN) return;
  active = true;
  loadGoogleAnalytics(GA_MEASUREMENT_ID, GOOGLE_ADS_ID);
  loadFacebookPixel(FB_PIXEL_ID);
  loadPinterest(PINTEREST_TAG_ID);
  loadProfitWell(PROFITWELL_TOKEN);
}

/** Attach the signed-in user across all loaded providers. */
export function identifyMarketing(user: MarketingUser) {
  if (!active) return;
  identifyGoogleAnalytics(user);
  identifyFacebookPixel(user, FB_PIXEL_ID);
  identifyPinterest(user, PINTEREST_TAG_ID);
  identifyProfitWell(user.email);
}

/** Emit a page view across all loaded providers for an SPA navigation. */
export function trackMarketingPageView(location: { path: string; title?: string }) {
  if (!active) return;
  trackGoogleAnalyticsPageView(location);
  trackFacebookPageView();
  trackPinterestPageVisit();
}

/** Registration conversion — fired once on a successful signup. */
export function trackSignupConversion() {
  if (!active) return;
  trackGoogleSignup(GOOGLE_ADS_ID);
  trackFacebookCompleteRegistration();
  trackPinterestSignup();
}

/** Subscribe conversion — fired once when a new subscription is created. */
export function trackSubscribeConversion(details: SubscribeDetails) {
  if (!active) return;
  trackGoogleSubscribe(GOOGLE_ADS_ID, details);
  trackFacebookSubscribe(details);
  trackPinterestCheckout(details);
}

/** Best-effort clear of user association on sign-out. */
export function resetMarketing() {
  if (!active) return;
  resetGoogleAnalytics();
}
